import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "@/shared/auth/useAuth";
import { detectDevice } from "@/shared/utils/deviceDetection";
import {
  SessionService,
  type SessionCheckResult,
} from "@/shared/services/sessionService";
import { getSupabase } from "@veltro/shared/lib/supabase";
import { logger } from "@/shared/utils/logger";

const SESSION_STORAGE_KEY = "veltro_session_id";
const HEARTBEAT_INTERVAL_MS = 60_000; // 60 seconds
const VALIDITY_CHECK_INTERVAL_MS = 30_000; // 30 seconds (was 120s — reduced for faster kick detection)

/** Termination reasons that mean "you were kicked — do NOT re-login automatically" */
const KICKED_REASONS = new Set(["kicked", "replaced_by_new_session", "access_denied"]);

/**
 * Check if a terminated session was kicked (should block) vs timed out (allow re-login).
 * Returns true if the session was deliberately kicked by another login or admin action.
 * Returns false if it timed out, was cleaned up, or doesn't exist anymore.
 */
async function wasSessionKicked(sessionId: string): Promise<boolean> {
  const info = await SessionService.getSessionStatus(sessionId);
  // Session row deleted (cron cleanup) or still active → not kicked
  if (!info || info.status === "active") return false;
  // Terminated — check the reason
  return KICKED_REASONS.has(info.terminatedReason ?? "");
}

export interface SessionManagerState {
  sessionId: string | null;
  sessionCheck: SessionCheckResult | null;
  isSessionActive: boolean;
  isInitializing: boolean;
  isTerminated: boolean;
  initializeSession: () => Promise<void>;
  logout: () => Promise<void>;
}

export function useSessionManager(): SessionManagerState {
  const { user, session, tenantId, licenseType, isSystemAdmin, signOut } = useAuth();
  const { t } = useTranslation("session");

  const [sessionId, setSessionId] = useState<string | null>(
    () => localStorage.getItem(SESSION_STORAGE_KEY)
  );
  const [sessionCheck, setSessionCheck] = useState<SessionCheckResult | null>(null);
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isTerminated, setIsTerminated] = useState(false);

  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const validityRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);
  const initInFlightRef = useRef(false);
  const initializeSessionRef = useRef<(() => Promise<void>) | null>(null);
  const reInitAttemptsRef = useRef(0);
  const MAX_REINIT_ATTEMPTS = 3;

  // ── Interval management ──────────────────────────────────────────────────

  const stopIntervals = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    if (validityRef.current) {
      clearInterval(validityRef.current);
      validityRef.current = null;
    }
  }, []);

  const startIntervals = useCallback(
    (sid: string) => {
      stopIntervals();

      heartbeatRef.current = setInterval(async () => {
        await SessionService.sendHeartbeat(sid);
      }, HEARTBEAT_INTERVAL_MS);

      validityRef.current = setInterval(async () => {
        const valid = await SessionService.isSessionValid(sid);
        if (!valid && mountedRef.current) {
          stopIntervals();

          const kicked = await wasSessionKicked(sid);
          localStorage.removeItem(SESSION_STORAGE_KEY);
          setSessionId(null);
          setIsSessionActive(false);

          if (kicked) {
            logger.error("[useSessionManager] Session kicked externally");
            setIsTerminated(true);
          } else if (reInitAttemptsRef.current < MAX_REINIT_ATTEMPTS) {
            reInitAttemptsRef.current += 1;
            logger.error("[useSessionManager] Session timed out, re-initializing (attempt " + reInitAttemptsRef.current + ")");
            initInFlightRef.current = false;
            initializeSessionRef.current?.();
          } else {
            logger.error("[useSessionManager] Max re-init attempts reached");
            setIsTerminated(true);
          }
        }
      }, VALIDITY_CHECK_INTERVAL_MS);
    },
    [stopIntervals]
  );

  // ── Initialize session ───────────────────────────────────────────────────

  const initializeSession = useCallback(async () => {
    if (!user?.id || !tenantId || !licenseType) return;

    // Prevent concurrent calls (race between restore effect and SessionGate)
    if (initInFlightRef.current) return;
    initInFlightRef.current = true;

    setIsInitializing(true);
    setSessionCheck(null);
    setIsTerminated(false);

    try {
      // Check localStorage first — a valid session may already exist (same browser, new tab)
      const storedId = localStorage.getItem(SESSION_STORAGE_KEY);
      if (storedId) {
        const valid = await SessionService.isSessionValid(storedId);
        if (!mountedRef.current) return;
        if (valid) {
          setSessionId(storedId);
          setIsSessionActive(true);
          reInitAttemptsRef.current = 0;
          startIntervals(storedId);
          SessionService.sendHeartbeat(storedId);
          return;
        }

        // Session is invalid — check WHY before deciding what to do
        const kicked = await wasSessionKicked(storedId);
        localStorage.removeItem(SESSION_STORAGE_KEY);
        if (kicked) {
          // This device was deliberately kicked — show terminated overlay, do NOT fight back
          setSessionId(null);
          setIsSessionActive(false);
          setIsTerminated(true);
          return;
        }
        // Timeout/ghost cleanup — allow fresh login below
      }

      const deviceInfo = detectDevice();

      // V2 flow: checkSessionAccess handles admin bypass, ghost cleanup, and auto-kick
      const check = await SessionService.checkSessionAccess(
        user.id,
        licenseType,
        deviceInfo,
        isSystemAdmin,
      );

      if (!mountedRef.current) return;
      setSessionCheck(check);

      if (!check.allowed) {
        // Only operator_desktop_blocked reaches here — auto-kick is handled internally
        setIsSessionActive(false);
        setIsInitializing(false);
        return;
      }

      const result = await SessionService.createSession(user.id, tenantId, licenseType, deviceInfo);
      if (!mountedRef.current) return;

      if (result.success) {
        localStorage.setItem(SESSION_STORAGE_KEY, result.sessionId);
        setSessionId(result.sessionId);
        setIsSessionActive(true);
        reInitAttemptsRef.current = 0;
        startIntervals(result.sessionId);

        // Show toast AFTER session is created, so the user sees it on their new dashboard
        if (check.kickedDeviceName !== undefined) {
          toast.info(t("autoKick.notification", { device: check.kickedDeviceName ?? t("devices.unknown") }));
        }
      } else {
        // Fail open — let user through even if session creation fails
        setIsSessionActive(true);
      }
    } catch (err) {
      logger.error("[useSessionManager] initializeSession failed:", err);
      // Fail open
      if (mountedRef.current) setIsSessionActive(true);
    } finally {
      initInFlightRef.current = false;
      if (mountedRef.current) setIsInitializing(false);
    }
  }, [user?.id, tenantId, licenseType, isSystemAdmin, startIntervals, t]);

  // Keep ref in sync for use in startIntervals validity check callback
  initializeSessionRef.current = initializeSession;

  // ── Logout ───────────────────────────────────────────────────────────────

  const logout = useCallback(async () => {
    stopIntervals();
    const sid = sessionId ?? localStorage.getItem(SESSION_STORAGE_KEY);
    if (sid && user?.id) {
      await SessionService.terminateCurrentSession(sid, user.id);
    }
    localStorage.removeItem(SESSION_STORAGE_KEY);
    setSessionId(null);
    setIsSessionActive(false);
    setIsTerminated(false);
    await signOut();
  }, [sessionId, user?.id, signOut, stopIntervals]);

  // ── Restore session from localStorage on mount ─────────────────────────

  useEffect(() => {
    const storedId = localStorage.getItem(SESSION_STORAGE_KEY);
    if (storedId && user?.id) {
      // Set initializing synchronously to block SessionGate from calling initializeSession
      setIsInitializing(true);
      initInFlightRef.current = true;

      // Validate that the stored session is still active
      SessionService.isSessionValid(storedId).then(async (valid) => {
        if (!mountedRef.current) return;
        if (valid) {
          setSessionId(storedId);
          setIsSessionActive(true);
          startIntervals(storedId);
          // Send an immediate heartbeat on restore
          SessionService.sendHeartbeat(storedId);
        } else {
          // Session is dead — check if kicked or timed out
          const kicked = await wasSessionKicked(storedId);
          localStorage.removeItem(SESSION_STORAGE_KEY);
          setSessionId(null);

          if (kicked) {
            // Kicked — show terminated overlay, block re-init
            setIsSessionActive(false);
            setIsTerminated(true);
          }
          // If not kicked (timeout/ghost), leave isSessionActive=false and isTerminated=false
          // so SessionGate's effect will call initializeSession() for a fresh login
        }
        initInFlightRef.current = false;
        setIsInitializing(false);
      });
    }
  }, [user?.id, startIntervals]);

  // ── Tab close → terminate session synchronously ─────────────────────────
  // `pagehide` fires reliably on mobile and desktop when a tab is closed,
  // unlike `beforeunload`. We use fetch() with keepalive so the termination
  // request survives the page unload — this prevents "ghost sessions" from
  // persisting in active_sessions until pg_cron sweeps them up to an hour
  // later (audit item #9).
  const unloadDataRef = useRef<{
    sessionId: string | null;
    userId: string | null;
    accessToken: string | null;
  }>({ sessionId: null, userId: null, accessToken: null });

  useEffect(() => {
    unloadDataRef.current = {
      sessionId,
      userId: user?.id ?? null,
      accessToken: session?.access_token ?? null,
    };
  }, [sessionId, user?.id, session?.access_token]);

  useEffect(() => {
    const handler = () => {
      const { sessionId: sid, userId: uid, accessToken } = unloadDataRef.current;
      if (!sid || !uid) return;
      const supabase = getSupabase();
      if (!supabase) return;
      const sb = supabase as unknown as { supabaseUrl: string; supabaseKey: string };
      if (!sb.supabaseUrl || !sb.supabaseKey) return;
      try {
        fetch(`${sb.supabaseUrl}/rest/v1/rpc/terminate_session`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: sb.supabaseKey,
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({
            p_session_id: sid,
            p_user_id: uid,
            p_reason: "user_logout",
          }),
          keepalive: true,
        });
      } catch {
        // Best-effort — pg_cron will eventually clean stale rows
      }
    };
    window.addEventListener("pagehide", handler);
    return () => window.removeEventListener("pagehide", handler);
  }, []);

  // ── Cleanup on unmount ───────────────────────────────────────────────────

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopIntervals();
    };
  }, [stopIntervals]);

  return {
    sessionId,
    sessionCheck,
    isSessionActive,
    isInitializing,
    isTerminated,
    initializeSession,
    logout,
  };
}
